import React, { useState, useEffect, useCallback } from "react";
import Confetti from "react-confetti";
import GameBoard from "./components/GameBoard";
import GameSummary from "./components/GameSummary";
import { MAX_GUESSES, ITEMS_PER_DAY, MAX_GUESS_VALUE, rateGuess, saveGameResult } from "./utils/gameHelpers";
import { playGuess, playCorrect, playWrong, playLoss, playFirstTry, playPhew } from "./utils/sounds";
import { supabase } from "./supabase";
import "./game.css";

function Game({ user, hardModeSlots, soundOn, onSlotChange }) {
  const [dailyItems, setDailyItems] = useState([]);
  const [currentSlot, setCurrentSlot] = useState(1);
  const [guess, setGuess] = useState("");
  const [slotStates, setSlotStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [pop, setPop] = useState(false);
  const [shake, setShake] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});

  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const todayKey = `erewhondle-${todayStr}`;

  // Load daily items from Supabase
  useEffect(() => {
    async function loadDailyItems() {
      setLoading(true);
      setFetchError(null);

      // In tests or misconfigured environments, Supabase may be mocked partially.
      // Avoid crashing and let the app render with no daily items.
      const dailyItemsQuery = supabase?.from?.("daily_items");
      if (!dailyItemsQuery?.select) {
        setDailyItems([]);
        setLoading(false);
        return;
      }

      const { data, error } = await dailyItemsQuery
        .select("slot, item_id, items(id, name, price, image_url, category, brand, subcategory, size, description, attributes, estimated_price, unit_price, is_weighted, ingredients, allergens)")
        .eq("date", todayStr)
        .order("slot");

      if (error) {
        console.error("Error loading daily items:", error);
        setFetchError("Failed to load today's items. Please try again.");
        setLoading(false);
        return;
      }

      setDailyItems(data || []);

      // Load saved state from localStorage
      const saved = localStorage.getItem(todayKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSlotStates(parsed.slotStates || {});
          setCurrentSlot(parsed.currentSlot || 1);
          setShowSummary(parsed.showSummary || false);
        } catch (e) { /* ignore bad data */ }
      }

      // Also load from Supabase if user is logged in
      if (user && supabase?.from) {
        const statsQuery = supabase.from("game_stats");
        const { data: stats } = await statsQuery
          .select("slot, result, guesses, guess_data")
          .eq("user_id", user.id)
          .eq("date", todayStr);

        if (stats && stats.length > 0) {
          const restored = {};
          stats.forEach((s) => {
            restored[s.slot] = {
              guesses: s.guess_data || [],
              status: s.result === "won" ? "won" : "lost",
            };
          });
          setSlotStates(restored);

          // Find next unplayed slot
          const playedSlots = new Set(stats.map((s) => s.slot));
          let next = 1;
          while (next <= ITEMS_PER_DAY && playedSlots.has(next)) next++;
          if (next > ITEMS_PER_DAY) {
            setShowSummary(true);
          }
          setCurrentSlot(next <= ITEMS_PER_DAY ? next : ITEMS_PER_DAY);
        } else {
          // No Supabase data — sync any completed localStorage slots to Supabase
          const saved = localStorage.getItem(todayKey);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              const localSlots = parsed.slotStates || {};
              for (const [slot, state] of Object.entries(localSlots)) {
                if (state.status === "won" || state.status === "lost") {
                  const dailyItem = (data || []).find((d) => d.slot === Number(slot));
                  if (dailyItem?.items) {
                    saveGameResult(user.id, todayStr, Number(slot), dailyItem.items.id, state.status, state.guesses.length, state.guesses);
                  }
                }
              }
            } catch (e) { /* ignore bad data */ }
          }
        }
      }

      setLoading(false);
    }

    loadDailyItems();
  }, [user?.id, todayStr, todayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist state to localStorage
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(todayKey, JSON.stringify({ slotStates, currentSlot, showSummary }));
    }
  }, [slotStates, currentSlot, showSummary, todayKey, loading]);

  // Preload all item images so slots 2-5 load instantly
  useEffect(() => {
    if (dailyItems.length > 0) {
      dailyItems.forEach((d) => {
        if (d.items?.image_url) {
          const img = new Image();
          img.src = d.items.image_url;
        }
      });
    }
  }, [dailyItems]);

  // Pop animation on guess count change
  const currentState = slotStates[currentSlot] || { guesses: [], status: "playing" };
  const hardMode = !!hardModeSlots?.[currentSlot];

  // Notify App of slot changes so the 🔥 button stays in sync
  useEffect(() => {
    onSlotChange(currentSlot, currentState.guesses.length > 0 || currentState.status !== "playing");
  }, [currentSlot, currentState.guesses.length, currentState.status, onSlotChange]);
  useEffect(() => {
    if (currentState.guesses.length > 0) {
      setPop(true);
      const timeout = setTimeout(() => setPop(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [currentState.guesses.length]);

  // Confetti on win
  useEffect(() => {
    if (currentState.status === "won") {
      setShowConfetti(true);
      const timeout = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [currentState.status]);

  const currentDaily = dailyItems.find((d) => d.slot === currentSlot);
  const currentItem = currentDaily?.items;

  const handleChange = (e) => {
    let value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      const dotIndex = value.indexOf(".");
      if (dotIndex >= 0 && value.length > dotIndex + 3) {
        value = value.slice(0, dotIndex + 3);
      }
      // Strip leading zeros (but keep "0" and "0.xx")
      if (dotIndex < 0 && value.length > 1 && value[0] === "0") {
        value = value.replace(/^0+/, "");
      }
      setGuess(value);
    }
  };

  const triggerShake = useCallback(() => {
    setShake(true);
    if (soundOn) playWrong();
    setTimeout(() => setShake(false), 500);
  }, [soundOn]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!currentItem || currentState.status !== "playing") return;

    const numericGuess = parseFloat(guess);
    const target = (currentItem.is_weighted && currentItem.estimated_price != null)
      ? currentItem.estimated_price
      : currentItem.price;

    if (isNaN(numericGuess) || numericGuess <= 0) {
      triggerShake();
      setGuess("");
      return;
    }

    if (numericGuess > MAX_GUESS_VALUE) {
      triggerShake();
      setGuess("");
      return;
    }

    const alreadyGuessed = currentState.guesses.some((g) => g.guess === numericGuess);
    if (alreadyGuessed) {
      triggerShake();
      setGuess("");
      return;
    }

    const feedback = rateGuess(numericGuess, target);

    const updatedGuesses = [...currentState.guesses, { guess: numericGuess, feedback }];
    let newStatus = "playing";

    if (feedback === "correct") {
      newStatus = "won";
    } else if (updatedGuesses.length >= MAX_GUESSES) {
      newStatus = "lost";
    }

    const newSlotStates = {
      ...slotStates,
      [currentSlot]: { guesses: updatedGuesses, status: newStatus },
    };
    setSlotStates(newSlotStates);

    // Sound effects
    if (soundOn) {
      if (newStatus === "won" && updatedGuesses.length === 1) {
        playFirstTry();
      } else if (newStatus === "won" && updatedGuesses.length >= MAX_GUESSES) {
        playPhew();
      } else if (newStatus === "won") {
        playCorrect();
      } else if (newStatus === "lost") {
        playLoss();
      } else if (feedback !== "correct") {
        playGuess();
      }
    }

    if (newStatus !== "playing" && user) {
      saveGameResult(user.id, todayStr, currentSlot, currentItem.id, newStatus, updatedGuesses.length, updatedGuesses);
    }

    setGuess("");
  };

  const advanceToNext = () => {
    if (allDone) {
      setShowSummary(true);
      return;
    }
    for (let i = currentSlot + 1; i <= ITEMS_PER_DAY; i++) {
      const s = slotStates[i];
      if (!s || s.status === "playing") {
        setCurrentSlot(i);
        setShowConfetti(false);
        return;
      }
    }
    for (let i = 1; i < currentSlot; i++) {
      const s = slotStates[i];
      if (!s || s.status === "playing") {
        setCurrentSlot(i);
        setShowConfetti(false);
        return;
      }
    }
    setShowSummary(true);
  };

  const goToSlot = (slot) => {
    setCurrentSlot(slot);
  };

  const allDone = Object.keys(slotStates).length >= ITEMS_PER_DAY &&
    Object.values(slotStates).every((s) => s.status !== "playing");

  if (loading) {
    return (
      <div className="game-container">
        <div className="progress-bar">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="progress-dot skeleton-pulse" />
          ))}
        </div>
        <div className="item-display">
          <div className="skeleton-text skeleton-round" />
          <div className="skeleton-image" />
          <div className="skeleton-text skeleton-name" />
        </div>
        <div className="game-meta">
          <div className="skeleton-text" style={{ width: "140px" }} />
          <div className="skeleton-text" style={{ width: "180px", marginTop: "4px" }} />
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="game-container">
        <p className="error-text">{fetchError}</p>
        <button className="next-btn" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (!dailyItems.length) {
    return (
      <div className="game-container">
        <p className="loading-text">No items found for today. Check back tomorrow!</p>
      </div>
    );
  }

  // Summary view after all 5 are done
  if (showSummary && allDone) {
    return (
      <GameSummary
        slotStates={slotStates}
        dailyItems={dailyItems}
        todayStr={todayStr}
        hardModeSlots={hardModeSlots}
        soundOn={soundOn}
        user={user}
      />
    );
  }

  return (
    <div className="game-container">
      <div className={`confetti-wrapper ${showConfetti ? "fade-in" : "fade-out"}`}>
        <Confetti recycle={false} numberOfPieces={hardMode ? 500 : 300} colors={hardMode ? ["#ff6b00", "#ff9500", "#ffcc00", "#ff4500", "#ff0000"] : undefined} />
      </div>

      {currentItem && (
        <GameBoard
          currentItem={currentItem}
          currentSlot={currentSlot}
          currentState={currentState}
          dailyItems={dailyItems}
          slotStates={slotStates}
          hardMode={hardMode}
          pop={pop}
          shake={shake}
          guess={guess}
          allDone={allDone}
          onSlotClick={goToSlot}
          imageLoaded={imageLoaded}
          onImageLoad={(slot) => setImageLoaded((prev) => ({ ...prev, [slot]: true }))}
          onGuessChange={handleChange}
          onSubmit={handleSubmit}
          onAdvance={advanceToNext}
        />
      )}
    </div>
  );
}

export default Game;
